export class AbortError extends Error {
  constructor() {
    super('Throttled function aborted');
    this.name = 'AbortError';
  }
}

export interface ThrottleOptions {
  limit: number;
  interval: number;
  strict?: boolean;
  onDelay?: (...args: any[]) => void;
}

/**
 * Represents a throttled function with control properties.
 */
// The ThrottledFunction interface is no longer returned by the throttle method
// and can be removed or commented out.
/*
export interface ThrottledFunction<F extends (...args: any[]) => any> {
  (...args: Parameters<F>): Promise<ReturnType<F>>;
  /** Enable or disable throttling */
/*
  isEnabled: boolean;
  /** Number of queued calls */
/*
  readonly queueSize: number;
  /** Abort all pending calls */
/*
  abort(): void;
}
*/

export class BetterThrottler {
  private limit: number;
  private interval: number;
  private strict: boolean;
  private onDelay?: (...args: any[]) => void;

  private queue = new Map<ReturnType<typeof setTimeout>, (reason?: any) => void>();
  private strictTicks: number[] = [];
  private currentTick = 0;
  private activeCount = 0;

  /** Globally enable/disable this throttler */
  public isEnabled = true;

  constructor({ limit, interval, strict = false, onDelay }: ThrottleOptions) {
    if (!Number.isFinite(limit) || limit < 1) {
      throw new TypeError('Expected `limit` to be a finite number greater than or equal to 1');
    }
    if (!Number.isFinite(interval)) {
      throw new TypeError('Expected `interval` to be a finite number');
    }

    this.limit = limit;
    this.interval = interval;
    this.strict = strict;
    this.onDelay = onDelay;
  }

  private windowedDelay(): number {
    const now = Date.now();

    // Handle time moving backwards (e.g., mocked dates, system clock changes)
    // If now is significantly before currentTick, reset the window
    if (now < this.currentTick - this.interval) {
        this.currentTick = now;
        this.activeCount = 1;
        return 0;
    }

    // Case 1: The current window (defined by currentTick) has completely passed,
    // or currentTick is in the past and we are starting fresh for a new window.
    if (now >= this.currentTick + this.interval) {
        this.currentTick = now; // Start a new window from 'now'.
        this.activeCount = 1;  // This is the first call in the new window.
        return 0;              // No delay.
    }

    // Case 2: We are operating within the conceptual window anchored by currentTick.
    // This window might be [currentTick, currentTick + interval).
    // 'now' could be < currentTick if currentTick was pushed into the future.

    if (this.activeCount < this.limit) {
        // There's capacity in the window associated with currentTick.
        this.activeCount++;
        // If currentTick is in the future, we must delay until currentTick.
        // If currentTick is in the past (or 'now'), no delay from this specific check.
        return Math.max(0, this.currentTick - now);
    } else {
        // Limit reached for the window starting at currentTick.
        // Advance currentTick to the start of the next available window.
        this.currentTick += this.interval;
        this.activeCount = 1; // This call is the first in that new (future) window.
        // The delay is until this new window starts.
        return this.currentTick - now;
    }
  }

  private strictDelay(): number {
    const now = Date.now();

    // Handle time moving backwards - clear all ticks if time jumped backward significantly
    if (this.strictTicks.length > 0 && now < this.strictTicks.at(-1)! - this.interval) {
      this.strictTicks.length = 0;
    }

    if (this.strictTicks.length > 0 && now - this.strictTicks.at(-1)! > this.interval) {
      this.strictTicks.length = 0;
    }

    // With limit >= 1, this.strictTicks[0] is safe to access when strictTicks.length >= limit
    // because limit cannot be 0.
    if (this.strictTicks.length < this.limit) {
      this.strictTicks.push(now);
      return 0;
    }

    const nextTime = this.strictTicks[0] + this.interval;
    this.strictTicks.shift();
    this.strictTicks.push(nextTime);

    return Math.max(0, nextTime - now);
  }

  private getDelay(): number {
    return this.strict ? this.strictDelay() : this.windowedDelay();
  }

  /**
   * Executes a function with throttling behavior.
   * The function `fn` is expected to be a thunk (takes no arguments).
   * Returns a Promise that resolves with the result of `fn()`.
   */
  public throttle<T>(fn: () => T | Promise<T>): Promise<T> {
    if (!this.isEnabled) {
      try {
        return Promise.resolve(fn());
      } catch (error) {
        return Promise.reject(error);
      }
    }

    return new Promise<T>((resolve, reject) => {
      const delay = this.getDelay();

      const task = () => {
        try {
          const result = fn();
          resolve(result);
        } catch (error) {
          reject(error);
        }
      };

      if (delay > 0) {
        let timeout_id!: ReturnType<typeof setTimeout>; // Assigned immediately below
        timeout_id = setTimeout(() => {
          task();
          this.queue.delete(timeout_id);
        }, delay);

        this.queue.set(timeout_id, (reason?: any) => {
          clearTimeout(timeout_id);
          // No need to explicitly delete from queue here if abort() clears the whole queue.
          // However, if a promise is rejected individually not via abort(), it should be cleaned.
          // For consistency with abort(), let abort() handle the full queue clear.
          // If this reject is called outside of abort (e.g. future feature), then queue.delete(timeout_id) here.
          reject(reason || new AbortError());
        });

        this.onDelay?.(); // Call onDelay without arguments
      } else {
        task();
      }
    });
  }

  /** Aborts all pending executions in this throttler */
  public abort(): void {
    for (const [timeout_id, reject_promise_fn] of this.queue) {
      clearTimeout(timeout_id);
      reject_promise_fn(new AbortError());
    }
    this.queue.clear();
    this.strictTicks.length = 0;
  }

  /** Number of currently queued calls */
  public get queueSize(): number {
    return this.queue.size;
  }
}
